import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { ArrowLeft, Users } from "lucide-react";
import { useTranslation } from "@/i18n";


export default function AdminUsers() {
  const [, navigate] = useLocation();
  const listQuery = trpc.adminUsers.list.useQuery(undefined, { staleTime: 10_000 });
  const utils = trpc.useUtils();
  const { t } = useTranslation();

  const [filter, setFilter] = useState("");
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "user" as "user" | "admin", isActive: false, password: "" });

  const [activateUser, setActivateUser] = useState<any | null>(null);
  const [activateQuota, setActivateQuota] = useState<number>(50);

  const createMutation = trpc.adminUsers.create.useMutation({
    onSuccess: async () => {
      toast.success(t.adminUsers.toasts.created);
      await utils.adminUsers.list.invalidate();
      setNewUser({ email: "", name: "", role: "user", isActive: true, password: "" });
    },
    onError: (e) => toast.error(e.message || t.adminUsers.toasts.createFailed),
  });
  const updateMutation = trpc.adminUsers.update.useMutation({
    onSuccess: async () => {
      toast.success(t.adminUsers.toasts.updated);
      await utils.adminUsers.list.invalidate();
    },
    onError: (e) => toast.error(e.message || t.adminUsers.toasts.updateFailed),
  });
  const setActiveMutation = trpc.adminUsers.setActive.useMutation({
    onSuccess: async () => {
      await utils.adminUsers.list.invalidate();
    },
    onError: (e) => toast.error(e.message || t.adminUsers.toasts.activateFailed),
  });
  const setRoleMutation = trpc.adminUsers.setRole.useMutation({
    onSuccess: async () => {
      await utils.adminUsers.list.invalidate();
    },
    onError: (e) => toast.error(e.message || t.adminUsers.toasts.roleFailed),
  });
  const deleteMutation = trpc.adminUsers.delete.useMutation({
    onSuccess: async () => {
      toast.success(t.adminUsers.toasts.deleted);
      await utils.adminUsers.list.invalidate();
    },
    onError: (e) => toast.error(e.message || t.adminUsers.toasts.deleteFailed),
  });

  const users = useMemo(() => {
    const data = listQuery.data ?? [];
    if (!filter) return data;
    const f = filter.toLowerCase();
    return data.filter((u: any) => u.email.toLowerCase().includes(f) || u.name?.toLowerCase()?.includes(f));
  }, [listQuery.data, filter]);

  useEffect(() => {
    // client-side guard placeholder; server enforces adminProcedure
  }, []);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => {
              if (window.history.length > 1) {
                window.history.back();
              } else {
                navigate("/dashboard");
              }
            }}
            className="gap-2 hover:bg-purple-50 dark:hover:bg-purple-950/30 hover:text-purple-600 dark:hover:text-purple-400 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> {t.adminUsers.back}
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-purple-600" />
            {t.adminUsers.title}
          </h1>
        </div>
        <div className="w-64">
          <Input placeholder={t.adminUsers.searchPlaceholder} value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t.adminUsers.createTitle}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t.adminUsers.email}</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{t.adminUsers.name}</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{t.adminUsers.password}</Label>
              <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{t.adminUsers.role}</Label>
              <Select value={newUser.role} onValueChange={(v: string) => setNewUser({ ...newUser, role: v as "user" | "admin" })}>
                <SelectTrigger>
                  <SelectValue placeholder={t.adminUsers.role} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">{t.adminUsers.roleUser}</SelectItem>
                  <SelectItem value="admin">{t.adminUsers.roleAdmin}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input id="active" type="checkbox" checked={newUser.isActive} onChange={(e) => setNewUser({ ...newUser, isActive: e.target.checked })} />
            <Label htmlFor="active">{t.adminUsers.active}</Label>
          </div>
          <Button onClick={() => createMutation.mutate(newUser)} disabled={createMutation.isPending}>
            {t.adminUsers.create}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t.adminUsers.listTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 px-3">{t.adminUsers.email}</th>
                  <th className="py-2 px-3">{t.adminUsers.name}</th>
                  <th className="py-2 px-3">{t.adminUsers.role}</th>
                  <th className="py-2 px-3">{t.adminUsers.active}</th>
                  <th className="py-2 px-3">{t.adminUsers.actions}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u: any) => (
                  <tr key={u.id} className="border-b">
                    <td className="py-2 px-3">
                      <Input defaultValue={u.email} onBlur={(e) => updateMutation.mutate({ userId: u.id, email: e.target.value })} />
                    </td>
                    <td className="py-2 px-3">
                      <Input defaultValue={u.name} onBlur={(e) => updateMutation.mutate({ userId: u.id, name: e.target.value })} />
                    </td>
                    <td className="py-2 px-3">
                      <Select defaultValue={u.role} onValueChange={(v: string) => setRoleMutation.mutate({ userId: u.id, role: v as "user" | "admin" })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">{t.adminUsers.roleUser}</SelectItem>
                          <SelectItem value="admin">{t.adminUsers.roleAdmin}</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-2 px-3">
                      <input
                        type="checkbox"
                        checked={u.isActive}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Show dialog for quota assignment when activating
                            setActivateUser(u);
                            setActivateQuota(50);
                          } else {
                            // Deactivate directly
                            setActiveMutation.mutate({ userId: u.id, isActive: false });
                          }
                        }}
                      />
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex gap-2">
                        <Button variant="destructive" onClick={() => deleteMutation.mutate({ userId: u.id })}>
                          {t.adminUsers.delete}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td className="py-4 px-3 text-muted-foreground" colSpan={5}>
                      {t.adminUsers.noneFound}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>


      {/* User Activation Dialog with Quota */}
      <Dialog open={!!activateUser} onOpenChange={(open) => { if (!open) setActivateUser(null); }}>
        <DialogContent>
          <DialogHeader>
              <DialogTitle>{t.adminUsers.activateUser}</DialogTitle>
            <DialogDescription>
                {t.adminUsers.activateUserDescription}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="activate-quota">
                  {t.adminUsers.storageQuota} (GB)
              </Label>
              <Input
                id="activate-quota"
                type="number"
                min="1"
                max="10000"
                value={activateQuota}
                onChange={(e) => setActivateQuota(parseFloat(e.target.value) || 50)}
              />
              <p className="text-sm text-muted-foreground">
                  {t.adminUsers.storageQuotaHelp}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActivateUser(null)}
            >
                {t.common.cancel || "Cancel"}
            </Button>
            <Button
              onClick={() => {
                if (activateUser) {
                  setActiveMutation.mutate({
                    userId: activateUser.id,
                    isActive: true,
                    storageQuotaGB: activateQuota,
                  });
                  setActivateUser(null);
                }
              }}
            >
                {t.adminUsers.activate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
